import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Container, Sprite } from '@pixi/react';
import { textureChangeFrame } from '../../../core/helpers/textureChangeFrame';

import { menuSectionStore } from '../../../store/menuSectionStore';
import { getGameImages } from '../../../core/helpers/getGameImages';
import { MenuIconsId } from '../../../core/constants/menuIconsId';
import { isMobile } from '../../../core/helpers/isMobile';
import { iframeService } from '../../../services/iframeService';
import { PopupTypes } from '../../../core/constants/popupTypes';
import { applicationStore } from '../../../store/applicationStore';
import { soundService } from '../../../services/soundService';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { DefaultQueryParams, LicenseTypes } from '../../../core/constants/defaultQueryParams';
import { parseQuery } from '../../../core/helpers/parseQuery';

const MenuIcons = observer(({ x = 0, y, margin }) => {
  const { selectedIconId, setSelectedIconId, setIsShowMenuPopup } = menuSectionStore;
  const { isDemo } = applicationStore;
  const {
    homeIconHover,
    homeIconDefault,
    homeIconDisable,
    homeIconSelected,
    homeIconDefaultBig,
    homeIconSelectedBig,
    payTableIconHover,
    payTableIconDefault,
    payTableIconDisable,
    payTableIconSelected,
    payTableIconDefaultBig,
    payTableIconSelectedBig,
    rulesIconHover,
    rulesIconDefault,
    rulesIconDisable,
    rulesIconSelected,
    rulesIconDefaultBig,
    rulesIconSelectedBig,
    historyIconHover,
    historyIconDefault,
    historyIconDisable,
    historyIconSelected,
  } = useMemo(() => {
    return getGameImages();
  }, []);

  const menuNavigationIcons = useMemo(() => {
    return isDemo
      ? [
          {
            id: MenuIconsId.Home,
            image: {
              default: isMobile() ? homeIconDefaultBig : homeIconDefault,
              hover: homeIconHover,
              disable: homeIconDisable,
              selected: isMobile() ? homeIconSelectedBig : homeIconSelected,
            },
          },
          {
            id: MenuIconsId.PayTable,
            image: {
              default: isMobile() ? payTableIconDefaultBig : payTableIconDefault,
              hover: payTableIconHover,
              selected: isMobile() ? payTableIconSelectedBig : payTableIconSelected,
              disable: payTableIconDisable,
            },
          },

          {
            id: MenuIconsId.GameRules,
            image: {
              default: isMobile() ? rulesIconDefaultBig : rulesIconDefault,
              hover: rulesIconHover,
              selected: isMobile() ? rulesIconSelectedBig : rulesIconSelected,
              disable: rulesIconDisable,
            },
          },
        ]
      : [
          {
            id: MenuIconsId.Home,
            image: {
              default: homeIconDefault,
              hover: homeIconHover,
              disable: homeIconDisable,
              selected: homeIconSelected,
            },
          },
          {
            id: MenuIconsId.PayTable,
            image: {
              default: payTableIconDefault,
              hover: payTableIconHover,
              selected: payTableIconSelected,
              disable: payTableIconDisable,
            },
          },
          {
            id: MenuIconsId.GameHistory,
            image: {
              default: historyIconDefault,
              hover: historyIconHover,
              selected: historyIconSelected,
              disable: historyIconDisable,
            },
          },
          {
            id: MenuIconsId.GameRules,
            image: {
              default: rulesIconDefault,
              hover: rulesIconHover,
              selected: rulesIconSelected,
              disable: rulesIconDisable,
            },
          },
        ];
  }, [
    rulesIconDefault,
    rulesIconHover,
    rulesIconDisable,
    rulesIconSelected,
    homeIconDefault,
    homeIconDisable,
    homeIconHover,
    homeIconSelected,
    payTableIconDefault,
    payTableIconDisable,
    payTableIconHover,
    payTableIconSelected,
    homeIconDefaultBig,
    homeIconSelectedBig,
    payTableIconDefaultBig,
    payTableIconSelectedBig,
    rulesIconDefaultBig,
    rulesIconSelectedBig,
  ]);

  const handlePointerover = useCallback(
    (e) => {
      const { id } = e.target;
      if (id === selectedIconId) return;
      textureChangeFrame(e.target, menuNavigationIcons.find((el) => el.id === id).image.hover);
    },
    [selectedIconId, menuNavigationIcons]
  );

  const handlePointerOut = useCallback(
    (e) => {
      const { id } = e.target;
      if (id === selectedIconId) return;
      textureChangeFrame(e.target, menuNavigationIcons.find((el) => el.id === id).image.default);
    },
    [selectedIconId, menuNavigationIcons]
  );

  const handlePointerUp = useCallback(
    (e) => {
      const { [DefaultQueryParams.LicenseType]: licenseType } = parseQuery(window.location.search);
      const { id } = e.target;
      if (id === MenuIconsId.Home) {
        iframeService.redirectHome();
      } else if (id === MenuIconsId.GameHistory) {
        userSettingsStore.setIsSoundOn(false);
        soundService.stopAll();
        setIsShowMenuPopup(false);
        window.parent.postMessage(
          {
            name: PopupTypes.OPEN_SLOT_HISTORY,
            payload: { type: +licenseType === LicenseTypes.brazilianGlI ? 'lines-lab' : 'lines' },
          },
          '*'
        );
      } else {
        setSelectedIconId(id);
        textureChangeFrame(e.target, menuNavigationIcons.find((el) => el.id === id).image.selected);
      }
    },
    [setSelectedIconId, menuNavigationIcons]
  );
  return (
    <Container name='MenuIconsContainer' x={x} y={y}>
      {(isMobile() ? menuNavigationIcons : menuNavigationIcons.slice(1)).map(
        ({ image, id }, index) => {
          return (
            <Sprite
              id={id}
              key={id}
              anchor={0.5}
              interactive
              cursor='pointer'
              y={isMobile() ? 0 : index * margin}
              x={isMobile() ? index * margin : 0}
              texture={id === selectedIconId ? image.selected : image.default}
              pointerup={handlePointerUp}
              pointerover={handlePointerover}
              pointerout={handlePointerOut}
            />
          );
        }
      )}
    </Container>
  );
});

export default MenuIcons;
